import { StatusBar } from 'expo-status-bar';
import { useState } from 'react';
import { Platform, View, StyleSheet } from 'react-native';

import { BookingSummary } from './ConfirmBooking';
import { CampItemResponse } from '../api/camps';
import { useKidsSync } from '../components/BookingCalendarContainer';
import { ModalContainer } from '../components/BookingCalendarModal';
import { PrimaryButton } from '../components/Button';
import CancellationPolicyCheckbox from '../components/CancellationPolicyCheckbox';
import KidsInput, { Kid } from '../components/KidsInput';
import { Container, Header, InnerContainer } from '../components/Layout';
import Row from '../components/Row';
import {
  TextCtaButton,
  TextH2,
  TextH3,
  TextBodySmall,
  TextBody,
} from '../components/StyledText';
import { Divider } from '../components/Themed';
import BookingReceiptRow from '../components/confirm_booking/BookingReceiptRow';
import CareCreditCheckbox from '../components/confirm_booking/CareCreditCheckbox';
import CartItemSection from '../components/confirm_booking/CartItemSection';
import EmbeddedWaitingModal, {
  useEmbeddedWaitingModal,
} from '../components/confirm_booking/EmbeddedWaitingModal';
import LocationDetailsWrapper from '../components/confirm_booking/LocationDetailsWrapper';
import PriceSummaryWithCoupon from '../components/confirm_booking/PriceSummaryWithCoupon';
import Colors from '../constants/Colors';
import { useAuthDispatch } from '../contexts/authentication';
import { useCheckoutState, useCheckoutStateUpdate } from '../contexts/checkout';
import { useEmbedded } from '../contexts/embedded';
import { useLocation } from '../contexts/locations';
import { useSnackbarDispatch } from '../contexts/snackbar';
import { useCartPriceInfoQuery } from '../hooks/camps/useCartPriceInfoQuery';
import { usePostBookingCampRequest } from '../hooks/camps/usePostBookingCampRequest';
import useAppNavigation from '../hooks/useAppNavigation';
import useColorScheme from '../hooks/useColorScheme';
import { useMaxWidth } from '../hooks/useResponsive';
import {
  invalidateBookings,
  useBookingStoreState,
  useFetchBookings,
} from '../store/bookings';
import { formatPrice } from '../utils/locations';

function useBookingRequest(
  locationId: number,
  campId: number,
  chosenKids: Kid[],
  isCareCreditUsed: boolean,
  onEmbeddedPayment?: () => void
) {
  const theme = useColorScheme();
  const { navigate } = useAppNavigation();

  const dispatchSnackbar = useSnackbarDispatch();
  const authDispatch = useAuthDispatch();
  const updateCheckoutState = useCheckoutStateUpdate();

  const { isEmbedded } = useEmbedded();
  const { isSubmitting, makeRequest } = usePostBookingCampRequest(
    campId,
    chosenKids.map((kid) => kid.id),
    isCareCreditUsed,
    {
      onUrlResponse: (url) => {
        if (isEmbedded) {
          onEmbeddedPayment?.();
          window.open(url, '_blank');
          return;
        }
        // redirect to stripe
        window.location.href = url;
      },
      onCheckoutResponse: (response) => {
        updateCheckoutState({
          chosenSlots: {},
          dailyState: undefined,
          weeklyState: undefined,
          bookingMode: undefined,
          locationId: undefined,
          locationNameId: undefined,
          slots: undefined,
        });
        authDispatch({ type: 'balance', value: response.careCreditBalance });
        authDispatch({ type: 'booked_location', value: locationId });
        invalidateBookings();
        navigate('Explore', { code: '10', sessionId: response.checkoutId });
      },
      onNotEnoughCapacity: () => {
        dispatchSnackbar({
          type: 'error',
          message: (
            <TextBodySmall style={{ color: Colors[theme].background }}>
              {`A session you were trying to book is no longer available. Please select another session`}
            </TextBodySmall>
          ),
        });
      },
    }
  );

  return { isSubmitting, makeRequest };
}

export function useDiscountApplied() {
  const [discountApplied, setDiscountApplied] = useState<{
    discount: number;
    discountType: 'percentage' | 'fixed';
  } | null>(null);
  return { discountApplied, setDiscountApplied };
}

export default function ConfirmCampBooking({
  locationNameId,
  locationId,
  camp,
  careCreditBalance,
}: {
  locationNameId: string;
  locationId: number;
  camp: CampItemResponse;
  careCreditBalance: number;
}) {
  const [bookings] = useBookingStoreState();
  useFetchBookings();

  const currentBookingsIds = bookings.upcoming
    .filter((b) => b.location.id === locationId)
    .map((b) => b.id);

  const location = useLocation(locationNameId);
  const { kids } = useCheckoutState();

  const isMobileDevice = useMaxWidth(430);
  const isMediumDevice = useMaxWidth(1000);
  const TitleText = isMobileDevice ? TextCtaButton : TextH2;

  const [showingKidsModal, setShowingKidsModal] = useState(false);

  const showKidsModal = () => {
    setShowingKidsModal(true);
  };

  // kids
  const chosenKids = kids.filter((kid) => kid.isChosen && kid.isEligible);
  const kidsPerId = chosenKids.reduce(
    (acc, kid) => ({
      ...acc,
      [kid.id]: kid,
    }),
    {} as Record<string, Kid>
  );
  const childIds = chosenKids.map((k) => k.id);

  const { cartPricingInfo, totalPrice } = useCartPriceInfoQuery(
    camp.id,
    childIds
  );

  const [terms, setTerms] = useState(false);
  const [isCareCreditUsed, setIsCareCreditUsed] = useState(false);

  const [discountApplied, setDiscountApplied] = useState<{
    discount: number;
    discountType: 'percentage' | 'fixed';
  } | null>(null);

  const { isEmbedded } = useEmbedded();
  const { embeddedWaitingModalVisible, showEmbeddedWaitingModal } =
    useEmbeddedWaitingModal();

  const { makeRequest: onSubmit, isSubmitting } = useBookingRequest(
    locationId,
    camp.id,
    chosenKids,
    isCareCreditUsed,
    showEmbeddedWaitingModal
  );

  const requiresPayment =
    !isCareCreditUsed ||
    (cartPricingInfo && careCreditBalance < Number(cartPricingInfo.total));

  return (
    <Container>
      {Platform.OS === 'web' ? (
        <Header shadow hideSearchIcon />
      ) : (
        <StatusBar style={Platform.OS === 'ios' ? 'dark' : 'auto'} />
      )}
      <KidsChooserModal
        isVisible={showingKidsModal}
        onClose={() => setShowingKidsModal(false)}
      />
      <InnerContainer>
        <View
          style={[
            !isMobileDevice && styles.inner,
            isMobileDevice && styles.innerMobile,
          ]}
        >
          <TitleText>Confirm Booking</TitleText>
          <Row style={[styles.body, isMediumDevice && styles.bodyMedium]}>
            <LocationDetailsWrapper locationNameId={locationNameId} />
            <View style={styles.grow}>
              <BookingSummary
                onEditPress={showKidsModal}
                chosenKids={chosenKids}
                previewComponent={null}
              />
              <Divider style={styles.divider} />

              <TextH3 style={styles.price}>Price Details</TextH3>

              {!cartPricingInfo ? (
                <TextBodySmall>Loading...</TextBodySmall>
              ) : null}

              {cartPricingInfo &&
                Object.keys(cartPricingInfo.days).map((day) => (
                  <CartItemSection
                    title={`${camp.start_date} - ${camp.end_date}`}
                    total={Number(cartPricingInfo.days[day]!.total)}
                  >
                    {Object.entries(cartPricingInfo.days[day]!.children).map(
                      ([childId, childPricing]) => (
                        <BookingReceiptRow
                          label={`${kidsPerId[childId]?.firstName}`}
                          rightComponent={
                            <TextBody>
                              {formatPrice(Number(childPricing[0]?.total))}
                            </TextBody>
                          }
                        />
                      )
                    )}
                  </CartItemSection>
                ))}

              <PriceSummaryWithCoupon
                total={totalPrice}
                careCreditBalance={
                  isCareCreditUsed ? careCreditBalance : undefined
                }
                discountApplied={discountApplied}
                setDiscountApplied={setDiscountApplied}
              />

              {careCreditBalance > 0 && cartPricingInfo && (
                <>
                  <Divider style={styles.divider} />
                  <CareCreditCheckbox
                    value={isCareCreditUsed}
                    onChange={setIsCareCreditUsed}
                    total={Number(cartPricingInfo.total)}
                    careCreditBalance={careCreditBalance}
                  />
                </>
              )}
              <Divider style={styles.bottomDivider} />
              <CancellationPolicyCheckbox
                value={terms}
                onChange={setTerms}
                weekly={true}
                cancelationTreshold={location?.cancellationThreshold}
              />

              <PrimaryButton
                style={styles.button}
                onPress={onSubmit}
                title={
                  isSubmitting
                    ? 'Booking...'
                    : !requiresPayment
                      ? 'Complete Booking'
                      : 'Proceed to Payment'
                }
                disabled={!terms || isSubmitting}
              />
            </View>
          </Row>
        </View>

        {isEmbedded && embeddedWaitingModalVisible && (
          <EmbeddedWaitingModal
            locationId={locationId}
            currentBookingsIds={currentBookingsIds}
          />
        )}
      </InnerContainer>
    </Container>
  );
}

function KidsChooserModal({
  isVisible,
  onClose,
}: {
  isVisible: boolean;
  onClose: () => void;
}) {
  const isMobileDevice = useMaxWidth(430);
  const { kids, onKidChosenChange } = useKidsSync();
  return (
    <ModalContainer isVisible={isVisible} close={onClose}>
      <View style={{ height: 500, padding: isMobileDevice ? 0 : 20 }}>
        <View
          style={{ flex: 1, justifyContent: 'center', alignItems: 'center' }}
        >
          <View style={{ width: '100%' }}>
            <KidsInput
              onChange={onKidChosenChange}
              kids={kids}
              isFetching={false}
              errorStatus={null}
              expanded
            />
          </View>
        </View>

        <PrimaryButton title="Save" onPress={onClose} />
      </View>
    </ModalContainer>
  );
}

const styles = StyleSheet.create({
  inner: {
    marginTop: 50,
    marginBottom: 120,
  },
  innerMobile: {
    marginVertical: 30,
  },

  body: {
    alignItems: 'flex-start',
    marginTop: 50,
    justifyContent: 'space-between',
  },
  bodyMedium: { flexDirection: 'column' },
  button: {
    height: 60,
  },

  grow: {
    flex: 1,
  },
  divider: {
    marginVertical: 30,
  },
  bottomDivider: {
    marginTop: 30,
  },
  price: {
    marginBottom: 15,
  },
});
